import { useMemo, useState } from 'react';

import { buildNavPath as buildNavPathHelper } from 'helpers/candidate_application/form-page-helpers';
import { selfieHelper } from 'helpers/candidate_application/pre-section-page-helpers';
import { useQueryParams } from 'hooks/query-params';
import { useCandidateApplicationUploadActions } from 'hooks/upload-actions';
import { AnimatePresence } from 'motion/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Import flow components
import Selfie from './components/Selfie/Selfie';
import SliderSideMotion from 'components/Motion/SliderSideMotion';

import {
  getCandidateApplication,
  saveCandidateChecksValues,
  selectCandidateApplication,
} from 'api/candidate-application-slice';

const componentMap = {
  Selfie,
  // Add more components as needed
};

const PreSubsectionFlowRenderer = ({
  componentName,
  sectionKey,
  pageIndex,
  candidate,
  brand,
  token,
  totalPages,
  currentPage,
  hasIndexPage,
  rejectedSelfieFileId,
  check_values,
  onPreSubsectionFlowComplete = () => {},
}) => {
  const navigate = useNavigate();
  const [admin] = useQueryParams(['admin']);
  const { signUpload, createUpload, deleteUpload } = useCandidateApplicationUploadActions(token);
  const dispatch = useDispatch();
  const { application } = useSelector(selectCandidateApplication);

  // Helper to build navigation path with admin parameter preserved
  const buildNavPath = path => buildNavPathHelper(path, admin);

  // Get current selfie ID from application state
  const currentSelfieId = useMemo(() => {
    return application?.other_uploads?.find(upload => upload.metadata?.selfie)?.id;
  }, [application?.other_uploads]);

  const helpers = {
    Selfie: selfieHelper,
  };

  const Component = componentMap[componentName];

  const [isBackward, setIsBackward] = useState(false);

  if (!Component) {
    console.error(`PreSubsectionFlow component "${componentName}" not found`);
    return (
      <div className="d-flex flex-column u-gap-large">
        <div className="d-flex flex-column u-gap-small">
          <h2 className="primary-text u-font-weight-500">Component Not Found</h2>
          <p className="secondary-text">
            The component "{componentName}" could not be found. Please check the component name.
          </p>
        </div>
        <div className="d-flex flex-row u-gap-medium">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`))}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Common navigation handlers for all flow components
  const navigationHandlers = {
    navigateToNextPage: () => {
      setIsBackward(false);
      const nextPage = currentPage + 1;
      if (nextPage < totalPages) {
        navigate(
          buildNavPath(`/form_submission/candidate/${sectionKey}/${token}/page/${nextPage}`),
        );
      } else {
        navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
      }
    },
    navigateToPreviousPage: () => {
      setIsBackward(true);
      const previousPage = currentPage - 1;
      if (previousPage >= 0) {
        navigate(
          buildNavPath(`/form_submission/candidate/${sectionKey}/${token}/page/${previousPage}`),
        );
      } else {
        navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
      }
    },
    navigateToDashboard: () => {
      setIsBackward(true);
      navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
    },
  };

  const onSave = async photo => {
    if (!photo || !candidate?.id) {
      console.error('[Selfie] save failed: no photo or candidate id');
      return;
    }

    let isSelfieFixed = false;

    if (rejectedSelfieFileId) {
      const filterRejectedSelfieFileId = check_values?.rejected_values?.files?.filter(
        id => id !== rejectedSelfieFileId,
      );

      await dispatch(
        saveCandidateChecksValues({
          token,
          data: {
            rejected_values: { ...check_values.rejected_values, files: filterRejectedSelfieFileId },
            selfie_fixed: true,
          },
        }),
      );

      isSelfieFixed = true;
    }

    try {
      // Delete old selfie first (if exists) before uploading new one
      // This ensures handleSelfieDeletion runs before handleSelfieUpload
      // Check both rejectedSelfieFileId and currentSelfieId
      const selfieToDelete = rejectedSelfieFileId || currentSelfieId;
      if (selfieToDelete) {
        await deleteUpload(selfieToDelete);
      }

      // Upload new selfie (this will trigger handleSelfieUpload in backend)
      await helpers[componentName]({
        photo,
        candidate,
        signUpload,
        createUpload,
        deleteUpload,
      });

      // After selfie upload, refetch application data to get updated id_upload_details
      // The backend will have recalculated face similarity for all affected ID uploads
      await dispatch(getCandidateApplication(token));

      // VerifyIdentity's page-index math assumes preSubsectionFlows is now empty once we
      // navigate past this page — tell it so before navigating, otherwise its frozen mount-time
      // snapshot never catches up and the navigate below just lands back on this same page.
      onPreSubsectionFlowComplete();

      if (isSelfieFixed && check_values?.rejected_values?.files?.length === 0) {
        navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
      } else {
        // We need to navigate to the first subsection page
        // Calculate the correct page: if there's an index page, go to page 1, otherwise go to page 0
        const firstSubsectionPage = hasIndexPage ? 1 : 0;

        console.info(
          '[PreSubsectionFlowRenderer] Selfie uploaded, navigating to first subsection page:',
          firstSubsectionPage,
        );
        setIsBackward(false);
        navigate(
          buildNavPath(
            `/form_submission/candidate/${sectionKey}/${token}/page/${firstSubsectionPage}`,
          ),
        );
      }
    } catch (error) {
      console.error('[Selfie] save failed:', error);
    }
  };

  return (
    <AnimatePresence mode="wait">
      <SliderSideMotion
        key={`_index_${sectionKey}`}
        isBackward={isBackward}
        delay={0.1}
        className="d-flex flex-column u-gap-large">
        <Component
          sectionKey={sectionKey}
          pageIndex={pageIndex}
          token={token}
          brand={brand}
          totalPages={totalPages}
          currentPage={currentPage}
          candidate={candidate}
          navigation={navigationHandlers}
          redirect={navigationHandlers.navigateToNextPage}
          onSave={onSave}
        />
      </SliderSideMotion>
    </AnimatePresence>
  );
};

export default PreSubsectionFlowRenderer;
